import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, message } from 'antd';
import { HeartOutlined, HeartFilled } from '@ant-design/icons';
import { useStores } from '../../stores';

/**
 * FavoriteButton - A reusable favorite/heart button
 * @param {string} type - 'product', 'service', or 'partner'
 * @param {number} itemId - The ID of the item
 * @param {string} size - Button size ('small', 'default', 'large')
 * @param {boolean} showText - Whether to show "Save" text
 * @param {object} style - Additional styles
 */
const FavoriteButton = observer(({ type, itemId, size = 'default', showText = false, style = {} }) => {
  const { favoriteStore, authStore } = useStores();
  const [loading, setLoading] = useState(false);

  // Check if item is favorited based on type
  const isFavorite = () => {
    switch (type) {
      case 'product':
        return favoriteStore.favoriteProductIds.has(itemId);
      case 'service':
        return favoriteStore.favoriteServiceIds.has(itemId);
      case 'partner':
        return favoriteStore.favoritePartnerIds.has(itemId);
      default:
        return false;
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation(); // Prevent card click events

    if (!authStore.isAuthenticated) {
      message.warning('Please login to save favorites');
      return;
    }

    setLoading(true);

    let result;
    switch (type) {
      case 'product':
        result = await favoriteStore.toggleFavoriteProduct(itemId);
        break;
      case 'service':
        result = await favoriteStore.toggleFavoriteService(itemId);
        break;
      case 'partner':
        result = await favoriteStore.toggleFavoritePartner(itemId);
        break;
      default:
        setLoading(false);
        return;
    }

    setLoading(false);

    if (result.success) {
      message.success(result.isFavorite ? 'Added to favorites' : 'Removed from favorites');
    } else {
      message.error('Failed to update favorites');
    }
  };

  const favorite = isFavorite();

  return (
    <Button
      type={favorite ? 'primary' : 'default'}
      icon={favorite ? <HeartFilled /> : <HeartOutlined />}
      size={size}
      loading={loading}
      onClick={handleClick}
      style={{
        color: favorite ? '#fff' : '#ff4d4f',
        borderColor: '#ff4d4f',
        backgroundColor: favorite ? '#ff4d4f' : 'transparent',
        ...style
      }}
    >
      {showText && (favorite ? 'Saved' : 'Save')}
    </Button>
  );
});

export default FavoriteButton;
